import { useCallback, useEffect, useRef } from 'react';
import { history } from 'umi';
import { getInDevProjectList } from '@sg/services/globalpedia';
import { buildLiveTitlesUrl } from '@sg/pages/StudioManagement/LiveTitles/utils/liveTitlesUrl';
// @ts-ignore
import { storage } from '@gp/utils/utils';
import { STRATEGIC_STORAGE_KEY } from '@sg/constants/storageKeys';
import {
  PortfolioOverviewTab,
  StudiosTab,
  createBtnPoint,
  SGBuriedBtnPoint,
} from '@sg/utils/buriedPoint';
import type { IPipelineProject, TSegmentType } from '../type';

interface IInDevProjectItem {
  project_id: string | number;
  project: string;
}

export const usePipelineProjectNavigation = (
  segment: TSegmentType,
  isStudioPage = false,
) => {
  const inDevProjectListRef = useRef<IInDevProjectItem[]>([]);
  const scrollStorageKey = isStudioPage
    ? STRATEGIC_STORAGE_KEY.STUDIOS_PIPELINE_CALENDAR_SCROLL
    : STRATEGIC_STORAGE_KEY.PORTFOLIO_PIPELINE_CALENDAR_SCROLL;
  const tabConfig = isStudioPage ? StudiosTab : PortfolioOverviewTab;

  useEffect(() => {
    getInDevProjectList({})
      .then((res) => {
        inDevProjectListRef.current = res || [];
      })
      .catch(() => {
        inDevProjectListRef.current = [];
      });
  }, []);

  const resolveInDevProjectId = async (
    projectName: string,
  ): Promise<string | null> => {
    if (!inDevProjectListRef.current.length) {
      try {
        const res = await getInDevProjectList({});
        inDevProjectListRef.current = res || [];
      } catch {
        return null;
      }
    }

    const matched = inDevProjectListRef.current.find(
      (item) => item.project === projectName,
    );
    return matched?.project_id != null ? String(matched.project_id) : null;
  };

  const handleProjectCoverClick = useCallback(
    async (proj: IPipelineProject): Promise<void> => {
      const projectName = proj.project;
      if (!projectName) {
        return;
      }

      // Mark return target so Pipeline Calendar can be scrolled into view.
      storage.setSessionStorageItem(scrollStorageKey, 'true');

      const btnPoint = createBtnPoint(
        tabConfig,
        'pipelineCalendarProjectRedirectClick',
        `Pipeline Calendar Project Redirect - ${projectName}`,
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }

      if (segment === 'live') {
        history.push(
          buildLiveTitlesUrl({ liveView: 'business', gameTitle: projectName }),
        );
        return;
      }

      const projectId = await resolveInDevProjectId(projectName);
      if (!projectId) {
        return;
      }

      // Auto-scroll to GR Management after landing on In Dev project detail.
      storage.setSessionStorageItem(
        STRATEGIC_STORAGE_KEY.INDEV_GR_MANAGEMENT_ID,
        'true',
      );

      const params = new URLSearchParams();
      params.set('tab', 'studio');
      params.set('subtitle', 'projects');
      params.set('project_id', projectId);
      history.push(`/strategic/home?${params.toString()}`);
    },
    [scrollStorageKey, segment, tabConfig],
  );

  return { handleProjectCoverClick };
};
